import { get, post, remove } from '@apis/api';
import { CommonPageResponse } from '@wconcept/wconcept-common/models/common-page-response';
import {
  AggBrand,
  AggCategory,
  AggCategoryDetpth,
  AggPriceRange,
  AggsOption,
} from '@wconcept/wconcept-common/models/display/search';
import { CommonResponse } from '@wconcept/wconcept-common/models/event';
import {
  FilterAggs,
  FilterInfo,
} from '@wconcept/wconcept-common/models/renewal/common';
import {
  KewyordContent,
  StyleClip,
} from '@wconcept/wconcept-common/models/renewal/discovery';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  ProfileData,
  StyleclipContent,
  StyleclipKeyword,
  StyleClipProfileTab,
  StyleClipRecommendData,
  StyleClipRegistParam,
  StyleClipRegistResponse,
  StyleClipUpdateParam,
  StyleclipLpContentResultResponse,
  ContentItem,
  TabInfo,
  StyleclipPersonalServiceAgreement,
  KeywordTabCountInfo,
  StyleclipCreateContent,
  StyleclipPoint,
  StyleClipRecommendTab,
  HideProfile,
  StyleClipRankingCategory,
  StyleClipRankingCustomerProfileInfo,
  StyleClipRecommendCreatorListInfo,
  StyleClipProfileHideInfo,
  BestStyleClipInfo,
} from '@wconcept/wconcept-common/models/renewal/styleclip';

const baseURL = process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_URL;
const options = {
  headers: {
    'Content-Type': 'application/json; charset=utf-8', // 필요에 따라 헤더 수정
    'DISPLAY-API-KEY': process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
  },
};

interface PageParam {
  pageNo: number;
  pageSize: number;
}
export const requestStyleClipOrderProduct = async ({
  pageNo,
  pageSize,
  startDate,
  endDate,
}: PageParam & {
  startDate: string;
  endDate: string;
}) => {
  const { data } = await post<
    CommonResponse<CommonPageResponse<Product[]>>,
    {
      startDate: string;
      endDate: string;
      pageNo: number;
      pageSize: number;
    }
  >({
    url: `/display/api/styleclip/v1/order/product`,
    options,
    baseUrl: baseURL,
    data: {
      startDate,
      endDate,
      pageNo,
      pageSize,
    },
  });
  return data as CommonPageResponse<Product[]>;
};

interface StyleClipSearchProductParam {
  pageNo: number;
  pageSize: number;
  keyword: string;
  source: string;
  device: string;
  searchType: string;
}
export const requestStyleClipSearchProduct = async ({
  pageNo,
  pageSize,
  keyword,
  source,
  device,
  searchType,
}: StyleClipSearchProductParam) => {
  const { data } = await post<
    CommonResponse<CommonPageResponse<Product[]>>,
    StyleClipSearchProductParam
  >({
    url: `/display/api/styleclip/v1/search/product`,
    options,
    baseUrl: baseURL,
    data: {
      pageNo,
      pageSize,
      keyword,
      source,
      device,
      searchType,
    },
  });
  return data as CommonPageResponse<Product[]>;
};

// /display/api/styleclip/v1/content/register
export const requestStyleClipContentRegist = async (
  params: StyleClipRegistParam
) => {
  const { data } = await post<CommonResponse<StyleClipRegistResponse>>({
    url: `/display/api/styleclip/v1/content/register`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};
// /display/api/styleclip/v1/content/{id}
export const requestStyleClipContent = async ({
  id,
  isEditYn,
}: {
  id: string;
  isEditYn?: 'Y' | 'N';
}) => {
  const { data } = await get<CommonResponse<StyleclipContent>>({
    url:
      isEditYn === 'Y'
        ? `/display/api/styleclip/v1/content/${id}?isEditYn=Y`
        : `/display/api/styleclip/v1/content/${id}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};
interface ImageUploadResponse {
  customData: null;
  fileName: string;
  filePath: string;
  fileType: string;
  imageUrl: string;
}
// /display/api/styleclip/v1/upload/images
export const requestStyleClipUploadImages = async (params: {
  files: File[];
  type: string | 'styleClip';
}) => {
  const fmData = new FormData();
  params.files.forEach((file, index) => {
    fmData.append(`files[${index}]`, file); // 각 파일을 개별적으로 추가
  });
  fmData.append('type', params.type);
  const { data } = await post<CommonResponse<ImageUploadResponse[]>>({
    url: `/display/api/styleclip/v1/upload/images`,
    // options,
    options: {
      headers: {
        'DISPLAY-API-KEY':
          process.env.NEXT_PUBLIC_DISPLAY_API_GATEWAY_KEY || '',
      },
    },
    baseUrl: baseURL,
    data: fmData,
  });
  return data as ImageUploadResponse[];
};

export const requestStyleclipKeyword = async () => {
  const { data } = await get<CommonResponse<StyleclipKeyword[]>>({
    url: `/display/api/styleclip/v1/keyword`,
    options,
    baseUrl: baseURL,
  });
  return data as StyleclipKeyword[];
};

// /display/api/styleclip/v1/content/update
export const requestStyleClipContentUpdate = async (
  params: StyleClipUpdateParam
) => {
  const data = await post<CommonResponse<StyleClipRegistResponse>>({
    url: `/display/api/styleclip/v1/content/update`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/content/delete
export const requestStyleClipContentDelete = async (id: number) => {
  const res = await get<CommonResponse<StyleClipRegistResponse>>({
    url: `/display/api/styleclip/v1/content/delete/${id}`,
    options,
    baseUrl: baseURL,
  });
  return res;
};

export const requestStyleClipContentDeleteAfterApprove = async (id: number) => {
  const res = await remove<CommonResponse<StyleClipRegistResponse>>({
    url: `/display/api/styleclip/v1/content/modify/cancel/${id}`,
    options,
    baseUrl: baseURL,
  });
  return res;
};

// /display/api/styleclip/v1/profile/tab
export const requestStyleClipProfileTab = async (params: {
  profileSeqNo: number;
}) => {
  const { data } = await get<CommonResponse<StyleClipProfileTab>>({
    url: `/display/api/styleclip/v1/profile/${params.profileSeqNo}/tab`,
    options: {
      ...options,
      cache: 'no-cache',
    },
    baseUrl: baseURL,
  });
  return data;
};

interface StyleClipProfileContentListParam {
  pageNo: number;
  pageSize: number;
  profileSeqNo?: number;
}
// /display/api/styleclip/v1/profile/products
export const requestStyleClipProfileProductList = async (
  params: StyleClipProfileContentListParam
) => {
  const { data } = await post<CommonResponse<CommonPageResponse<Product[]>>>({
    url: `/display/api/styleclip/v1/profile/products`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/profile/contents
export const requestStyleClipProfileContentList = async (
  params: StyleClipProfileContentListParam
) => {
  const { data } = await post<
    CommonResponse<CommonPageResponse<StyleclipContent[]>>
  >({
    url: '/display/api/styleclip/v1/profile/contents',
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/profile/[id]
interface StyleClipProfileInfoParam {
  profileSeqNo: number;
}
export const requestStyleClipProfileInfo = async (
  params: StyleClipProfileInfoParam
) => {
  const { data } = await get<CommonResponse<ProfileData>>({
    url: `/display/api/styleclip/v1/profile/${params.profileSeqNo}`,
    options: {
      ...options,
      cache: 'no-cache',
    },
    baseUrl: baseURL,
  });
  return data;
};

/**
 * styleClip 신고하기
 */
export interface StyleClipReportRequestParam {
  styleClipId: number;
  reportCustNo: number;
  reportCustId: string;
  reportType: string;
  reportReason: string;
}

export type ReportResponse = {
  returnValue: number;
  returnMessage: string;
};

export const requestReportStyleClip = async (
  body: StyleClipReportRequestParam
) => {
  const { data } = await post<CommonResponse<ReportResponse>>({
    url: `/display/api/styleclip/v1/content/report/register`,
    data: body,
    baseUrl: baseURL,
    options,
  });

  return data;
};

// /display/api/styleclip/v1/keyword/content
export const requestGetStyleclipKeywordContent = async ({
  keyword,
}: // pageNo,
// pageSize,
{
  keyword: string;
  // pageNo: number;
  // pageSize: number;
}) => {
  const { data } = await post<
    CommonResponse<{
      items: KewyordContent[];
      page: number;
      pageSize: number;
      total: number;
    }>
  >({
    url: `/display/api/styleclip/v1/keyword/content`,
    data: {
      keyword,
      // pageNo,
      // pageSize,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /display/api/styleclip/v1/keyword/product
export const requestGetStyleclipKeywordProduct = async ({
  keyword,
  pageNo,
  pageSize,
}: {
  keyword: string;
  pageNo: number;
  pageSize: number;
}) => {
  const { data } = await post<CommonResponse<CommonPageResponse<Product[]>>>({
    url: `/display/api/styleclip/v1/keyword/product`,
    data: {
      keyword,
      pageNo,
      pageSize,
    },
    options,
    baseUrl: baseURL,
  });
  return data;
};

// config + 필터 초기 정보 + 필터네임 선택한 이름
// /display/api/styleclip/v1/lp/filter
export const requestGetStyleclipLpContentResult = async (params?: {
  source: string;
  device: string;
  searchType: string;
  sort?: string;
  pageNo?: number;
  pageSize?: number;
  styles?: string[];
  tpos?: string[];
  seasons?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  brandCds?: number[];
}) => {
  const { data } = await post<CommonResponse<StyleclipLpContentResultResponse>>(
    {
      url: `/display/api/styleclip/v1/lp/filter`,
      data: params || {},
      options,
      baseUrl: baseURL,
    }
  );
  return data;
};
//  필터 선택한것에 대한 리스트 호출
// /display/api/styleclip/v1/lp/contents
export const requestGetStyleclipLpContentList = async (params?: {
  source?: string;
  device?: string;
  searchType?: string;
  sort?: string;
  pageNo: number;
  pageSize: number;
  styles?: string[];
  tpos?: string[];
  seasons?: string[];
  mcd?: string;
  ccds?: string[];
  pcds?: string[];
  lcds?: string[];
  brandCds?: number[];
}) => {
  const { data } = await post<
    CommonResponse<CommonPageResponse<StyleclipContent>>
  >({
    url: `/display/api/styleclip/v1/lp/contents`,
    data: params || {},
    options,
    baseUrl: baseURL,
  });
  return data;
};

// 필터 선택할마다 호출하는 필터정보
// /display/api/styleclip/v1/lp/filter/{type}
// type = style, time-place, season, category, brand
export const requestGetStyleclipLpContentFilter = async (
  type:
    | 'style'
    | 'tpo'
    | 'season'
    | 'category'
    | 'brand'
    | 'new-brand'
    | 'tpo-brand'
    | string,
  params?: {
    source: string;
    device: string;
    searchType: string;
    sort: string;
    pageNo?: number;
    pageSize?: number;
    styles?: string[];
    tpos?: string[];
    seasons?: string[];
    mcd?: string;
    ccds?: string[];
    pcds?: string[];
    lcds?: string[];
    brandCds?: number[];
  }
) => {
  const { data } = await post<CommonResponse<FilterAggs>>({
    url: `/display/api/styleclip/v1/lp/filter/${type}`,
    options,
    baseUrl: baseURL,
    data: params || {},
  });
  return data;
};

//  주간 랭킹 키워드 페이지
// /display/api/styleclip/v1/styling/ranking/weekly/content
export const requestGetStyleclipKeywordRankingContent = async () => {
  const { data } = await get<CommonResponse<KewyordContent[]>>({
    url: `/display/api/styleclip/v1/styling/ranking/weekly/content`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// 스타일 상세쪽 스타일링
// display/api/styleclip/[id]/other-content
export const requestGetStyleclipProfileOrderContent = async (id: number) => {
  const { data } = await get<
    CommonResponse<{
      areaTitle1: string;
      landingUrl: string;
      contentList: {
        id: number;
        imageUrl: string;
        isThumbnailImage: boolean;
        landingUrl: string;
      }[];
    }>
  >({
    url: `/display/api/styleclip/v1/${id}/other-content`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// display/api/styleclip/v1/styling/recommend/tab
export const requestGetStyleclipRecommendTab = async () => {
  const { data } = await get<CommonResponse<StyleClipRecommendTab>>({
    url: `/display/api/styleclip/v1/styling/recommend/tab`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /display/api/styleclip/v1/styling/recommend/content
export const requestGetStyleclipRecommendStylingContent = async (params: {
  tabName: string;
}) => {
  const { data } = await post<CommonResponse<StyleClipRecommendData>>({
    url: `/display/api/styleclip/v1/styling/recommend/content`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/{id}/related/content
export const requestGetStyleclipRecommendRelatedContent = async (
  id: number
) => {
  const { data } = await get<
    CommonResponse<{
      title1: string;
      contentList: ContentItem[];
    }>
  >({
    url: `/display/api/styleclip/v1/${id}/relation/content`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /display/api/styleclip/v1/content/relation/product
export const requestGetStyleclipRelationProduct = async (
  relationProductList: string[]
) => {
  const data = await post<CommonResponse<Product[]>>({
    url: `/display/api/styleclip/v1/content/relation/product`,
    options,
    baseUrl: baseURL,
    data: {
      relationProductList,
    },
  });
  return data;
};

// /display/api/styleclip/v1/personal/agreement
export const requestGetStyleclipPersonalServiceAgreement = async () => {
  const { data } = await get<CommonResponse<StyleclipPersonalServiceAgreement>>(
    {
      url: `/display/api/styleclip/v1/personal/agreement`,
      options,
      baseUrl: baseURL,
    }
  );
  return data;
};

// /display/api/styleclip/v1/profile/myhearts
export const requestGetStyleclipMyHeartContentList = async (params: {
  pageNo: number;
  pageSize: number;
}) => {
  const { data } = await post<
    CommonResponse<CommonPageResponse<StyleclipContent[]>>
  >({
    url: `/display/api/styleclip/v1/profile/myhearts`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/profile/hide
export const requestStyleclipProfileHide = async (params: {
  hideProfileSeqNo: number;
}) => {
  const { result } = await post<CommonResponse<string>>({
    url: `/display/api/styleclip/v1/profile/hide`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return result;
};

export const requestStyleclipProfileHideList = async () => {
  const { data } = await get<CommonResponse<StyleClipProfileHideInfo>>({
    url: `/display/api/styleclip/v1/profile/hide`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /display/api/styleclip/v1/keyword/tab
export const requestStyleclipKeywordTab = async ({
  keyword,
}: {
  keyword: string;
}) => {
  const { data } = await post<CommonResponse<KeywordTabCountInfo>>({
    url: `/display/api/styleclip/v1/keyword/tab`,
    options: {
      ...options,
      cache: 'no-cache',
    },
    baseUrl: baseURL,
    data: {
      keyword,
    },
  });
  return data;
};

// /display/api/styleclip/v1/styling/ranking/weekly/tab
export const requestGetStyleclipStylingRankingWeeklyTab = async () => {
  const { data } = await get<CommonResponse<KeywordTabCountInfo>>({
    url: `/display/api/styleclip/v1/styling/ranking/weekly/tab`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

// /display/api/styleclip/v1/styling/ranking/weekly/product
export const requestGetStyleclipStylingRankingWeeklyProduct = async (params: {
  pageNo: number;
  pageSize: number;
}) => {
  const { data } = await post<CommonResponse<CommonPageResponse<Product[]>>>({
    url: `/display/api/styleclip/v1/styling/ranking/weekly/product`,
    options,
    baseUrl: baseURL,
    data: params,
  });
  return data;
};

// /display/api/styleclip/v1/point
export const requestGetStyleclipPoint = async () => {
  const { data } = await get<CommonResponse<StyleclipPoint[]>>({
    url: `/display/api/styleclip/v1/point`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestHideProfile = async (custNo: number) => {
  const { data } = await get<CommonResponse<HideProfile>>({
    url: `/display/api/customer/v1/styleclip/hide/${custNo}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export interface StyleClipRecommendContentResponse<T>
  extends CommonPageResponse<T> {
  uiData: {
    recommendKey: string;
    hideProfileSeqNos: number[];
    keywordLandingUrl: string;
  };
}

export const requestStyleClipRecommendContentList = async (body: {
  pageNo: number;
  pageSize: number;
  recommendKey: string;
  recommendItems: string[];
}) => {
  const { data } = await post<
    CommonResponse<StyleClipRecommendContentResponse<StyleclipContent[]>>
  >({
    url: `/display/api/styleclip/v2/recommend/content`,
    data: body,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestGetBestStyleClip = async (params: {
  depth1Code: string;
}) => {
  const { data } = await post<CommonResponse<BestStyleClipInfo>>({
    url: `/display/api/styleclip/v2/best/popular-style`,
    data: params,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestStyleClipRankingCategoryList = async (
  contentType: string
) => {
  const { data } = await get<CommonResponse<StyleClipRankingCategory[]>>({
    url: `/display/api/styleclip/v2/ranking/styling/category?contentType=${contentType}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestStyleClipRankingStylingStyleClipList = async (
  pageNo: number,
  pageSize: number,
  rankingType: string,
  contentType: string,
  depth1Code: string
) => {
  const { data } = await get<
    CommonResponse<CommonPageResponse<StyleclipContent[]>>
  >({
    url: `/display/api/styleclip/v2/ranking/styling?pageNo=${pageNo}&pageSize=${pageSize}&rankingType=${rankingType}&contentType=${contentType}&depth1Code=${depth1Code}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestStyleClipRankingCustomerStyleClipList = async (
  pageNo: number,
  pageSize: number,
  rankingType: string,
  contentType: string
) => {
  const { data } = await get<
    CommonResponse<CommonPageResponse<StyleClipRankingCustomerProfileInfo[]>>
  >({
    url: `/display/api/styleclip/v2/ranking/customer?pageNo=${pageNo}&pageSize=${pageSize}&rankingType=${rankingType}&contentType=${contentType}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export const requestStyleClipRankingBrandStyleClipList = async (
  pageNo: number,
  pageSize: number,
  rankingType: string
) => {
  const { data } = await get<
    CommonResponse<CommonPageResponse<StyleClipRankingCustomerProfileInfo[]>>
  >({
    url: `/display/api/styleclip/v2/ranking/brand?pageNo=${pageNo}&pageSize=${pageSize}&rankingType=${rankingType}`,
    options,
    baseUrl: baseURL,
  });
  return data;
};

export interface StyleClipRecommendCreatorResponse<T>
  extends CommonResponse<T> {
  baseData: number[];
}

export const requestStyleClipRecommendCreatorList = async (body: {
  recommendItems: string[];
}) => {
  const { data, baseData } = await post<
    StyleClipRecommendCreatorResponse<StyleClipRecommendCreatorListInfo>
  >({
    url: `/display/api/styleclip/v2/recommend/insights`,
    data: body,
    options,
    baseUrl: baseURL,
  });
  return { data, baseData };
};
